import {
  useLoaderData,
  useMatches,
  useRouteLoaderData,
} from "react-router";

import type { ShellLoader } from "./loader";

/** Stable id for the catch-all shell route — required for loader reads from `errorElement`. */
export const SHELL_ROUTE_ID = "tinkertoy-shell";

/**
 * The payload shape consumers can rely on when `useShellLoaderData()`
 * resolves. Derived from the loader's return type — the `Response` branch
 * used for redirects is excluded because React Router follows redirects
 * internally and never delivers a `Response` to `useLoaderData` at runtime.
 */
export type ShellLoaderData = Exclude<
  Awaited<ReturnType<ShellLoader>>,
  Response
>;

function isShellLoaderData(value: unknown): value is ShellLoaderData {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (!("loadedView" in value) || !("flowMeta" in value)) {
    return false;
  }
  const loadedView = value.loadedView;
  if (typeof loadedView !== "object" || loadedView === null) {
    return false;
  }
  return "viewId" in loadedView && typeof loadedView.viewId === "string";
}

function readShellLoaderDataFromMatches(
  matches: ReturnType<typeof useMatches>,
): ShellLoaderData | undefined {
  for (const match of matches) {
    if (isShellLoaderData(match.data)) {
      return match.data;
    }
  }
  return undefined;
}

function useShellLoaderDataFromRouteId(): ShellLoaderData | undefined {
  try {
    return useRouteLoaderData<ShellLoaderData>(SHELL_ROUTE_ID);
  } catch {
    return undefined;
  }
}

function useShellLoaderDataFromLoaderContext(): ShellLoaderData | undefined {
  try {
    const loaderData = useLoaderData<ShellLoader>();
    return isShellLoaderData(loaderData) ? loaderData : undefined;
  } catch {
    return undefined;
  }
}

function useShellLoaderDataFromMatches(): ShellLoaderData | undefined {
  try {
    return readShellLoaderDataFromMatches(useMatches());
  } catch {
    return undefined;
  }
}

/**
 * Reads shell loader data when present. Returns `undefined` on the error
 * boundary after a loader failure, or when invoked outside the shell router.
 */
export function useOptionalShellLoaderData(): ShellLoaderData | undefined {
  const fromRouteId = useShellLoaderDataFromRouteId();
  if (fromRouteId !== undefined) {
    return fromRouteId;
  }

  const fromLoaderContext = useShellLoaderDataFromLoaderContext();
  if (fromLoaderContext !== undefined) {
    return fromLoaderContext;
  }

  return useShellLoaderDataFromMatches();
}

/**
 * Reads the shell loader payload with compile-time typing tied to
 * {@link ShellLoader}. All shell-internal consumers of `useLoaderData`
 * should go through this helper so the loader's return type is the single
 * source of truth — if {@link ShellLoader} changes, every consumer becomes
 * a type error instead of a silent `unknown`-to-cast lie.
 *
 * Also surfaces a clear developer-facing error when used outside the
 * Tinkertoy shell's React Router context (e.g. a consumer renders a shell
 * component outside of the tree created by `mountShell`).
 */
export function useShellLoaderData(): ShellLoaderData {
  const data = useOptionalShellLoaderData();
  if (data === undefined) {
    throw new Error(
      "Shell loader data was requested outside the Tinkertoy shell. " +
        "Ensure the component is rendered within the tree created by mountShell().",
    );
  }

  return data;
}
